import { Accessibility, Languages, RotateCw, Sparkles } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { toast } from "sonner";

/**
 * Accessibility & Language settings card used on the Profile page.
 * - Dyslexia-friendly mode toggle
 * - Language toggle (EN / ES)
 * - Replay the welcome walkthrough
 */
const SettingsPanel = () => {
  const { dyslexiaMode, setDyslexiaMode, language, setLanguage, resetOnboarding, t } = useSettings();

  return (
    <div className="card-pop" data-testid="settings-panel">
      <h2 className="font-heading font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
        <Accessibility className="w-5 h-5 text-purple-500" />
        {t("settings_accessibility")}
      </h2>

      {/* Dyslexia Mode */}
      <div className="flex items-start justify-between gap-4 py-3 border-b border-slate-100">
        <div className="flex-1">
          <p className="font-bold text-slate-800">{t("settings_dyslexia")}</p>
          <p className="text-sm text-slate-500 mt-1">{t("settings_dyslexia_desc")}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={dyslexiaMode}
          onClick={async () => {
            const v = !dyslexiaMode;
            await setDyslexiaMode(v);
            toast.success(v ? "Dyslexia mode on" : "Dyslexia mode off");
          }}
          data-testid="dyslexia-toggle"
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
            dyslexiaMode ? "bg-indigo-500" : "bg-slate-300"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
              dyslexiaMode ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      {/* Language */}
      <div className="flex items-start justify-between gap-4 py-3 border-b border-slate-100">
        <div className="flex-1">
          <p className="font-bold text-slate-800 flex items-center gap-2">
            <Languages className="w-4 h-4" />
            {t("settings_language")}
          </p>
          <p className="text-sm text-slate-500 mt-1">{t("settings_language_desc")}</p>
        </div>
        <div className="flex items-center bg-slate-100 rounded-full p-1">
          <button
            onClick={() => setLanguage("en")}
            className={`px-3 py-1 rounded-full text-sm font-bold ${language === "en" ? "bg-indigo-500 text-white" : "text-slate-500"}`}
            data-testid="settings-lang-en"
          >
            EN
          </button>
          <button
            onClick={() => setLanguage("es")}
            className={`px-3 py-1 rounded-full text-sm font-bold ${language === "es" ? "bg-indigo-500 text-white" : "text-slate-500"}`}
            data-testid="settings-lang-es"
          >
            ES
          </button>
        </div>
      </div>

      {/* Replay walkthrough */}
      <div className="flex items-center justify-between gap-4 py-3">
        <div className="flex-1">
          <p className="font-bold text-slate-800 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-yellow-500" />
            Welcome Tour
          </p>
          <p className="text-sm text-slate-500 mt-1">{t("settings_replay_tour")}</p>
        </div>
        <button
          onClick={async () => {
            await resetOnboarding();
            toast.success("Reload the Dashboard to see the tour");
          }}
          className="flex items-center gap-1 px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-sm font-bold text-slate-700"
          data-testid="replay-tour-btn"
        >
          <RotateCw className="w-4 h-4" />
          Replay
        </button>
      </div>
    </div>
  );
};

export default SettingsPanel;
